import { supabase } from "@/lib/supabase";

export type EngagementEntityType = "event" | "venue";

export type EngagementEventType =
  | "interest_added"
  | "interest_removed"
  | "proposal_added"
  | "proposal_removed"
  | "view";

type LogEngagementEventInput = {
  entityType: EngagementEntityType;
  entityId: string;
  eventType: EngagementEventType;
  userId: string;
  squadId?: string | null;
};

const SESSION_VIEWED_EVENTS_KEY = "pulse.engagement_viewed_events";

function markEventViewedInSession(eventId: string): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(SESSION_VIEWED_EVENTS_KEY);
    const viewed = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
    if (viewed.has(eventId)) return false;
    viewed.add(eventId);
    sessionStorage.setItem(SESSION_VIEWED_EVENTS_KEY, JSON.stringify([...viewed]));
    return true;
  } catch {
    return false;
  }
}

/** Best-effort append-only log — never throws; failures are silent. */
export function logEngagementEvent(input: LogEngagementEventInput): void {
  void supabase
    .from("engagement_events")
    .insert({
      entity_type: input.entityType,
      entity_id: input.entityId,
      event_type: input.eventType,
      user_id: input.userId,
      squad_id: input.squadId ?? null,
    })
    .then(() => undefined, () => undefined);
}

/**
 * Log an event detail page view once per browser session per event.
 * Only authenticated users (RLS: auth.uid() = user_id). Guests are skipped.
 * Not used for home-feed cards or venue detail — only /event/:id counts as a view.
 */
export function logEventDetailView(eventId: string, userId: string | undefined): void {
  if (!userId) return;
  if (!markEventViewedInSession(eventId)) return;
  logEngagementEvent({
    entityType: "event",
    entityId: eventId,
    eventType: "view",
    userId,
  });
}
